import { createFileRoute, redirect } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../router";
import { useState, useMemo } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import {
  Search,
  BookOpen,
  Activity,
  Rocket,
  Code,
  Shield,
  MessageSquare,
  FileText,
  Leaf,
  TreePine,
  Wrench,
  FolderOpen,
  BarChart3,
  ExternalLink,
} from "lucide-react";

export const Route = createFileRoute("/_admin/documentation/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    // Session validated — component renders static documentation entries.
    // TODO: Replace with real docs API fetch in future phase.
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Documentation"),
  component: DocumentationPage,
});

// ── Types ─────────────────────────────────────────────────────────────────

type DocLifecycle = "living" | "garden" | "compost" | "maintenance";
type DocCategory =
  | "overview"
  | "changelog"
  | "development"
  | "technical"
  | "admin"
  | "community";

interface DocItem {
  id: string;
  title: string;
  description: string;
  category: DocCategory;
  lifecycle: DocLifecycle;
  lastUpdated: string;
  wordCount: number;
  badges?: string[];
}

const CATEGORY_CONFIG: Record<
  DocCategory,
  { icon: React.ReactNode; color: string; label: string }
> = {
  overview: {
    icon: <BookOpen className="h-4 w-4" />,
    color: "bg-blue-500",
    label: "Overview",
  },
  changelog: {
    icon: <Activity className="h-4 w-4" />,
    color: "bg-indigo-500",
    label: "Changelog",
  },
  development: {
    icon: <Rocket className="h-4 w-4" />,
    color: "bg-green-500",
    label: "Development",
  },
  technical: {
    icon: <Code className="h-4 w-4" />,
    color: "bg-orange-500",
    label: "Technical",
  },
  admin: {
    icon: <Shield className="h-4 w-4" />,
    color: "bg-red-500",
    label: "Admin",
  },
  community: {
    icon: <MessageSquare className="h-4 w-4" />,
    color: "bg-cyan-500",
    label: "Community",
  },
};

const LIFECYCLE_CONFIG: Record<
  DocLifecycle,
  { emoji: string; label: string; color: string }
> = {
  living: {
    emoji: "🌱",
    label: "Living",
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  garden: {
    emoji: "🌿",
    label: "Garden",
    color:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  },
  compost: {
    emoji: "🍂",
    label: "Compost",
    color:
      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  },
  maintenance: {
    emoji: "🛠️",
    label: "Maintenance",
    color:
      "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  },
};

// ── Sample Docs ───────────────────────────────────────────────────────────

const SAMPLE_DOCS: DocItem[] = [
  {
    id: "1",
    title: "Architecture Quick Reference",
    description:
      "High-level overview of the FlowState platform architecture, including data flow, state management, and deployment patterns.",
    category: "technical",
    lifecycle: "living",
    lastUpdated: "2026-03-20",
    wordCount: 2450,
    badges: ["Essential", "Start Here"],
  },
  {
    id: "2",
    title: "TanStack vs Electric Decision Tree",
    description:
      "Decision guide for choosing between TanStack Query and Electric SQL for data state management.",
    category: "technical",
    lifecycle: "living",
    lastUpdated: "2026-03-18",
    wordCount: 1800,
    badges: ["Essential"],
  },
  {
    id: "3",
    title: "Navigation State Management",
    description:
      "Canonical optimistic update pattern for navigation preferences and sidebar state.",
    category: "development",
    lifecycle: "living",
    lastUpdated: "2026-03-15",
    wordCount: 1200,
  },
  {
    id: "4",
    title: "Supabase Migration Guide",
    description:
      "Step-by-step guide for creating and applying Supabase database migrations with RLS policies.",
    category: "admin",
    lifecycle: "living",
    lastUpdated: "2026-03-10",
    wordCount: 3200,
    badges: ["Priority"],
  },
  {
    id: "5",
    title: "Theme System Documentation",
    description:
      "CSS variable-based theme system with 5 presets, glassmorphism effects, and dark mode support.",
    category: "development",
    lifecycle: "garden",
    lastUpdated: "2026-02-28",
    wordCount: 1650,
    badges: ["UI/UX"],
  },
  {
    id: "6",
    title: "Getting Started Guide",
    description:
      "Onboarding documentation for new developers joining the FlowState team.",
    category: "overview",
    lifecycle: "living",
    lastUpdated: "2026-03-01",
    wordCount: 2800,
    badges: ["Start Here"],
  },
  {
    id: "7",
    title: "Release Notes v2.4",
    description:
      "Latest release notes including TanStack Start migration progress, new admin features, and bug fixes.",
    category: "changelog",
    lifecycle: "living",
    lastUpdated: "2026-03-22",
    wordCount: 900,
    badges: ["Updated"],
  },
  {
    id: "8",
    title: "Community Guidelines",
    description:
      "Code of conduct, contribution guidelines, and community engagement best practices.",
    category: "community",
    lifecycle: "garden",
    lastUpdated: "2026-01-15",
    wordCount: 1400,
    badges: ["Social"],
  },
  {
    id: "9",
    title: "Mycelium Event Bus",
    description:
      "Documentation for the Mycelium event bus system, including event types, publishing, and subscription patterns.",
    category: "technical",
    lifecycle: "living",
    lastUpdated: "2026-03-12",
    wordCount: 2100,
  },
  {
    id: "10",
    title: "Legacy API Migration",
    description:
      "Guide for migrating from legacy REST API endpoints to the new server function architecture.",
    category: "technical",
    lifecycle: "compost",
    lastUpdated: "2025-09-01",
    wordCount: 1800,
  },
];

// ── Main Page ─────────────────────────────────────────────────────────────

function DocumentationPage() {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return <AuthenticatedDocumentation />;
}

// ── Authenticated sub-component ───────────────────────────────────────────

function AuthenticatedDocumentation() {
  const [searchQuery, setSearchQuery] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState<
    DocLifecycle | "all"
  >("all");

  const filteredDocs = useMemo(() => {
    return SAMPLE_DOCS.filter((doc) => {
      const matchesSearch =
        !searchQuery ||
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesLifecycle =
        lifecycleFilter === "all" || doc.lifecycle === lifecycleFilter;
      return matchesSearch && matchesLifecycle;
    });
  }, [searchQuery, lifecycleFilter]);

  // Group by category
  const groupedDocs = useMemo(() => {
    const groups: Record<DocCategory, DocItem[]> = {
      overview: [],
      changelog: [],
      development: [],
      technical: [],
      admin: [],
      community: [],
    };
    for (const doc of filteredDocs) {
      groups[doc.category].push(doc);
    }
    return groups;
  }, [filteredDocs]);

  // Stats
  const totalDocs = SAMPLE_DOCS.length;
  const categories = new Set(SAMPLE_DOCS.map((d) => d.category)).size;
  const avgWords = Math.round(
    SAMPLE_DOCS.reduce((sum, d) => sum + d.wordCount, 0) / totalDocs
  );

  // Staleness check (> 6 months old)
  const isStale = (dateStr: string) => {
    const date = new Date(dateStr);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return date < sixMonthsAgo;
  };

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Documentation</h1>
        <p className="text-muted-foreground mt-1">
          Browse, search, and manage platform documentation.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Docs
                </p>
                <p className="text-2xl font-bold">{totalDocs}</p>
              </div>
              <FileText className="h-5 w-5 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Categories
                </p>
                <p className="text-2xl font-bold">{categories}</p>
              </div>
              <FolderOpen className="h-5 w-5 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Avg Words
                </p>
                <p className="text-2xl font-bold">{avgWords}</p>
              </div>
              <BarChart3 className="h-5 w-5 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Living
                </p>
                <p className="text-2xl font-bold">
                  {SAMPLE_DOCS.filter((d) => d.lifecycle === "living").length}
                </p>
              </div>
              <Leaf className="h-5 w-5 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documentation..."
            className="pl-10"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={lifecycleFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setLifecycleFilter("all")}
          >
            All
          </Button>
          {(
            Object.entries(LIFECYCLE_CONFIG) as [
              DocLifecycle,
              (typeof LIFECYCLE_CONFIG)[DocLifecycle],
            ][]
          ).map(([key, config]) => (
            <Button
              key={key}
              variant={lifecycleFilter === key ? "default" : "outline"}
              size="sm"
              onClick={() => setLifecycleFilter(key)}
            >
              {config.emoji} {config.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filteredDocs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No documents found matching your search.
          </p>
          <Button
            variant="ghost"
            className="mt-2"
            onClick={() => {
              setSearchQuery("");
              setLifecycleFilter("all");
            }}
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Documents grouped by category */}
      {(
        Object.entries(groupedDocs) as [DocCategory, DocItem[]][]
      ).map(([category, docs]) => {
        if (docs.length === 0) return null;
        const config = CATEGORY_CONFIG[category];
        return (
          <div key={category}>
            <div className="flex items-center gap-2 mb-3">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded ${config.color} text-white`}
              >
                {config.icon}
              </div>
              <h2 className="text-lg font-semibold">{config.label}</h2>
              <Badge variant="secondary" className="text-xs">
                {docs.length}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {docs.map((doc) => (
                <Card
                  key={doc.id}
                  className="hover:border-primary/30 transition-colors cursor-pointer"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-sm">
                            {doc.title}
                          </h4>
                          <Badge
                            className={
                              LIFECYCLE_CONFIG[doc.lifecycle].color
                            }
                          >
                            {LIFECYCLE_CONFIG[doc.lifecycle].emoji}{" "}
                            {LIFECYCLE_CONFIG[doc.lifecycle].label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {doc.description}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{doc.wordCount} words</span>
                          <span
                            className={
                              isStale(doc.lastUpdated)
                                ? "text-yellow-600"
                                : ""
                            }
                          >
                            Updated: {doc.lastUpdated}
                          </span>
                        </div>
                        {doc.badges && doc.badges.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {doc.badges.map((badge) => (
                              <Badge
                                key={badge}
                                variant="outline"
                                className="text-xs"
                              >
                                {badge}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </main>
  );
}
